import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { PAYMENT_CURRENCIES } from '@hygge/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/errors';
import { useSavePaymentDetails, type PaymentDetails } from '@/lib/employee-finance';
import { invalidProps, withoutFieldError } from '@/features/people/lib/field-a11y';
import { useFocusFirstError } from '@/features/people/lib/use-focus-first-error';
import { FieldError } from './field-error';
import { serverFieldErrors } from '@/features/people/lib/server-field-errors';
import {
  PAYMENT_DETAILS_SERVER_FIELDS,
  toPaymentDetailsForm,
  toPaymentDetailsInput,
  validatePaymentDetails,
  type PaymentDetailsErrors,
  type PaymentDetailsForm,
} from '@/features/people/lib/payment-details-form';

interface PaymentDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string;
  details: PaymentDetails | null;
}

// Radix Select cannot hold an empty-string value, so "no currency" has a token of its own.
const NO_CURRENCY = '__none__';

const TEXT_FIELDS: {
  key: Exclude<keyof PaymentDetailsForm, 'currency' | 'registrationAddress' | 'kved'>;
  label: string;
  placeholder: string;
  required?: boolean;
}[] = [
  { key: 'fopName', label: 'FOP name', placeholder: 'ФОП Іваненко Іван Іванович', required: true },
  { key: 'iban', label: 'IBAN', placeholder: 'UA21 3223 1300 0002 6007 2335 6600 1' },
  { key: 'taxId', label: 'Tax ID (RNOKPP)', placeholder: '10 digits' },
  { key: 'swift', label: 'SWIFT / BIC', placeholder: 'PBANUA2X' },
];

/** Form keys and their element ids, in the order the form shows them. */
const FIELD_IDS = (
  ['fopName', 'iban', 'taxId', 'swift', 'currency', 'registrationAddress', 'kved'] as const
).map((key) => [key, `fop-${key}`] as const);

/** Add or edit the whole FOP block — the API replaces it in one PUT. [HW-1008] */
export function PaymentDetailsDialog({
  open,
  onOpenChange,
  employeeId,
  details,
}: PaymentDetailsDialogProps) {
  const [form, setForm] = useState<PaymentDetailsForm>(() => toPaymentDetailsForm(details));
  const [errors, setErrors] = useState<PaymentDetailsErrors>({});
  const save = useSavePaymentDetails(employeeId);
  const focusFirstError = useFocusFirstError(FIELD_IDS, errors, save.isPending);

  // Filled from `details` only as the dialog opens: a refetch while it is open
  // (someone else saved the block, a window-focus refetch) must not wipe what
  // is being typed.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setForm(toPaymentDetailsForm(details));
      setErrors({});
    }
    wasOpen.current = open;
  }, [open, details]);

  const set = (key: keyof PaymentDetailsForm, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => withoutFieldError(prev, key));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const found = validatePaymentDetails(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      focusFirstError();
      return;
    }
    save.mutate(toPaymentDetailsInput(form), {
      onSuccess: () => {
        toast.success('Payment details saved');
        onOpenChange(false);
      },
      onError: (err) => {
        // A 400 lands under the fields it names; only what names none is a toast.
        const mapped = serverFieldErrors(err, PAYMENT_DETAILS_SERVER_FIELDS);
        if (!mapped) {
          toast.error(extractErrorMessage(err, 'Failed to save payment details'));
          return;
        }
        setErrors(mapped.fields);
        focusFirstError();
        if (mapped.unmatched.length > 0) toast.error(mapped.unmatched.join('. '));
      },
    });
  };

  const fieldError = (key: keyof PaymentDetailsForm) => (
    <FieldError id={`fop-${key}`} error={errors[key]} />
  );
  const invalid = (key: keyof PaymentDetailsForm) => invalidProps(`fop-${key}`, errors[key]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{details ? 'Edit Payment Details' : 'Add Payment Details'}</DialogTitle>
          <DialogDescription>
            FOP details used to pay this employee. Only the FOP name is required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid gap-4 py-4">
            {TEXT_FIELDS.map(({ key, label, placeholder, required }) => (
              <div key={key} className="grid gap-2">
                <Label htmlFor={`fop-${key}`}>
                  {label} {required && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id={`fop-${key}`}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  placeholder={placeholder}
                  disabled={save.isPending}
                  className={errors[key] ? 'border-destructive' : ''}
                  {...invalid(key)}
                />
                {fieldError(key)}
              </div>
            ))}

            <div className="grid gap-2">
              <Label htmlFor="fop-currency">Currency</Label>
              <Select
                value={form.currency || NO_CURRENCY}
                onValueChange={(v) => set('currency', v === NO_CURRENCY ? '' : v)}
                disabled={save.isPending}
              >
                <SelectTrigger id="fop-currency" {...invalid('currency')}>
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CURRENCY}>Not set</SelectItem>
                  {PAYMENT_CURRENCIES.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {fieldError('currency')}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fop-registrationAddress">Registration address</Label>
              <Textarea
                id="fop-registrationAddress"
                value={form.registrationAddress}
                onChange={(e) => set('registrationAddress', e.target.value)}
                rows={2}
                disabled={save.isPending}
                className={errors.registrationAddress ? 'border-destructive' : ''}
                {...invalid('registrationAddress')}
              />
              {fieldError('registrationAddress')}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="fop-kved">KVED</Label>
              <Input
                id="fop-kved"
                value={form.kved}
                onChange={(e) => set('kved', e.target.value)}
                placeholder="62.01, 62.02"
                disabled={save.isPending}
                className={errors.kved ? 'border-destructive' : ''}
                {...invalid('kved')}
              />
              {fieldError('kved')}
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
